import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["mainMap"]
  static values = { 
    gigs: Array
  }

  connect() {
    this.isVisible = false
    this.miniMap = null
    this.observer = null
    
    // Attendre que la carte principale soit complètement initialisée avant de créer l'observer
    this.waitForMainMapToLoad()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.miniMap) {
      this.miniMap.remove()
    }
    this.removeMiniMapContainer()
  }

  waitForMainMapToLoad() {
    // Vérifier périodiquement si la carte principale est chargée
    const checkMainMap = () => {
      if (this.hasMainMapTarget) {
        // Vérifier si la carte principale contient des éléments de carte (canvas, markers, etc.)
        const mapCanvas = this.mainMapTarget.querySelector('.mapboxgl-canvas')
        const hasMapContent = mapCanvas && mapCanvas.width > 0

        if (hasMapContent) {
          // Créer le container et initialiser l'observer seulement maintenant
          this.createMiniMapContainer()
          this.initializeObserver()
        } else {
          // Réessayer dans 500ms
          setTimeout(checkMainMap, 500)
        }
      }
    }
    
    // Commencer la vérification après un court délai
    setTimeout(checkMainMap, 1000)
  }

  initializeObserver() {
    // Variable pour éviter les déclenchements immédiatement après l'initialisation
    this.observerInitialized = false
    
    // Observer pour détecter quand la carte principale sort de la vue
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const isMainMapVisible = entry.isIntersecting
        const visibilityRatio = entry.intersectionRatio

        // Ignorer le premier déclenchement qui survient à l'initialisation
        if (!this.observerInitialized) {
          this.observerInitialized = true
          return
        }
        
        // Utiliser un seuil plus strict pour vraiment s'assurer que la carte n'est plus visible
        const reallyNotVisible = !isMainMapVisible || visibilityRatio < 0.1
        const reallyVisible = isMainMapVisible && visibilityRatio > 0.3
        
        if (reallyVisible && this.isVisible) {
          // Carte principale vraiment visible → masquer mini-carte
          this.hideMiniMap()
        } else if (reallyNotVisible && !this.isVisible) {
          // Carte principale vraiment invisible → afficher mini-carte
          this.showMiniMap()
        }
      })
    }, { 
      threshold: [0, 0.1, 0.3, 0.5], // Seuils plus précis
      rootMargin: '0px 0px -50px 0px' // Moins agressif - trigger quand il reste 50px visible
    })

    // Observer la carte principale
    if (this.hasMainMapTarget) {
      this.observer.observe(this.mainMapTarget)
    }
  }

  createMiniMapContainer() {
    // Créer le container de la mini-carte s'il n'existe pas
    if (!document.getElementById('floating-mini-map-container')) {
      const container = document.createElement('div')
      container.id = 'floating-mini-map-container'
      
      // Styles inline pour le debug (pas de dépendance CSS externe)
      container.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 20px;
        width: 280px;
        height: 200px;
        z-index: 1000;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        border: 2px solid white;
        background: white;
        opacity: 0;
        transform: translateY(20px) scale(0.95);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        overflow: hidden;
        display: none;
      `
      
      // Ajouter le header
      const header = document.createElement('div')
      header.style.cssText = `
        background: white;
        border-bottom: 1px solid #e5e7eb;
        padding: 8px 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      `
      
      header.innerHTML = `
        <h4 style="font-size: 14px; font-weight: 600; color: #374151; margin: 0; display: flex; align-items: center;">
          🗺️ Carte de la tournée
        </h4>
        <button class="expand-btn" style="padding: 4px; border: none; background: none; color: #6b7280; cursor: pointer; border-radius: 4px;" title="Revenir à la carte principale">
          ↗️
        </button>
      `
      
      // Créer le container de la carte
      const mapContainer = document.createElement('div')
      mapContainer.id = 'floating-mini-map'
      mapContainer.style.cssText = `
        width: 100%;
        height: calc(100% - 40px);
      `
      
      container.appendChild(header)
      container.appendChild(mapContainer)
      document.body.appendChild(container)

      // Ajouter l'event listener pour le bouton expand
      const expandBtn = header.querySelector('.expand-btn')
      expandBtn.addEventListener('click', () => {
        this.scrollToMainMap()
      })
    }
  }

  removeMiniMapContainer() {
    const container = document.getElementById('floating-mini-map-container')
    if (container) {
      container.remove()
    }
  }

  showMiniMap() {
    if (this.isVisible) return
    
    this.isVisible = true
    
    const container = document.getElementById('floating-mini-map-container')
    if (!container) {
      return
    }

    // Afficher le container avec animation inline
    container.style.display = 'block'
    
    // Forcer un reflow puis animer
    container.offsetHeight
    container.style.opacity = '1'
    container.style.transform = 'translateY(0) scale(1)'

    // Initialiser la mini-carte Mapbox si elle n'existe pas
    if (!this.miniMap) {
      setTimeout(() => {
        this.initializeMiniMap()
      }, 100)
    }
  }

  hideMiniMap() {
    if (!this.isVisible) return
    
    this.isVisible = false
    
    const container = document.getElementById('floating-mini-map-container')
    if (!container) {
      return
    }

    // Masquer avec animation inline
    container.style.opacity = '0'
    container.style.transform = 'translateY(20px) scale(0.95)'
    
    // Masquer complètement après l'animation
    setTimeout(() => {
      if (!this.isVisible) { // Vérifier qu'on n'a pas changé d'avis entre temps
        container.style.display = 'none'
      }
    }, 300)
  }

  async initializeMiniMap() {
    // Vérifier que Mapbox est chargé
    if (typeof mapboxgl === 'undefined') {
      console.error("Mapbox GL JS n'est pas chargé")
      return
    }

    const mapContainer = document.getElementById('floating-mini-map')
    if (!mapContainer) return

    try {
      // Configurer le token d'accès
      mapboxgl.accessToken = this.authToken
      
      // Initialiser la mini-carte
      this.miniMap = new mapboxgl.Map({
        container: mapContainer,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [2.3522, 48.8566], // Paris par défaut
        zoom: 5,
        interactive: true,
        attributionControl: false, // Masquer l'attribution pour économiser l'espace
        projection: 'mercator' // Carte plate (pas de globe)
      })

      // Désactiver le zoom au scroll pour permettre le scroll de la page
      this.miniMap.scrollZoom.disable()

      // Ajouter les controls de navigation (version compacte)
      this.miniMap.addControl(new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true,
        visualizePitch: false
      }), 'top-right')

      this.miniMap.on('load', () => {
        this.addMiniMapMarkers()
      })
      
      this.miniMap.on('error', (e) => {
        console.error("Mini map error:", e)
      })

      // Redimensionner la carte après un court délai
      setTimeout(() => {
        if (this.miniMap) {
          this.miniMap.resize()
        }
      }, 100)
      
    } catch (error) {
      console.error("Erreur lors de l'initialisation de la mini-carte:", error)
    }
  }

  addMiniMapMarkers() {
    if (!this.gigsValue || this.gigsValue.length === 0 || !this.miniMap) {
      return
    }

    // Filtrer les gigs qui ont des coordonnées valides
    const gigsWithCoordinates = this.gigsValue.filter(gig =>
      gig.city && gig.city.trim() !== '' && gig.latitude && gig.longitude
    )

    if (gigsWithCoordinates.length === 0) {
      return
    }

    // Créer les markers pour chaque ville unique
    const cityGroups = this.groupGigsByCity(gigsWithCoordinates)
    const bounds = new mapboxgl.LngLatBounds()

    // Store coordinates in chronological order for route drawing
    const orderedCoordinates = []

    for (const [city, gigs] of Object.entries(cityGroups)) {
      // Utiliser les coordonnées du premier gig de cette ville
      const coordinates = [gigs[0].longitude, gigs[0].latitude]

      this.createMiniMarker(coordinates, city, gigs)
      bounds.extend(coordinates)

      // Store with order_index for sorting
      const orderIndex = Math.min(...gigs.map(gig => gig.order_index))
      orderedCoordinates.push({ coordinates, orderIndex })
    }

    // Sort coordinates by chronological order
    orderedCoordinates.sort((a, b) => a.orderIndex - b.orderIndex)

    // Draw driving route between all points
    if (orderedCoordinates.length >= 2) {
      this.drawDrivingRoute(orderedCoordinates.map(c => c.coordinates))
    }

    // Ajuster la vue de la mini-carte pour inclure tous les markers (sans animation)
    if (!bounds.isEmpty()) {
      this.miniMap.fitBounds(bounds, {
        padding: 20,
        maxZoom: 8, // Zoom moins élevé pour la mini-carte
        animate: false
      })
    }
  }

  groupGigsByCity(gigs) {
    return gigs.reduce((groups, gig) => {
      const city = gig.city.trim()
      if (!groups[city]) {
        groups[city] = []
      }
      groups[city].push(gig)
      return groups
    }, {})
  }

  createMiniMarker(coordinates, city, gigs) {
    // Créer le popup simplifié
    const popupContent = this.createMiniPopupContent(city, gigs)

    const popup = new mapboxgl.Popup({
      offset: 15,
      closeButton: false
    }).setHTML(popupContent)

    // Use the order_index of the first (earliest) gig in this city
    const orderIndex = Math.min(...gigs.map(gig => gig.order_index))

    // Créer l'élément marker plus petit
    const markerEl = document.createElement('div')
    markerEl.className = 'mini-touring-marker'
    markerEl.style.cssText = `
      background-color: #10b981;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 1px 4px rgba(0,0,0,0.3);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 10px;
    `
    markerEl.textContent = orderIndex.toString()

    // Créer le marker
    new mapboxgl.Marker(markerEl)
      .setLngLat(coordinates)
      .setPopup(popup)
      .addTo(this.miniMap)
  }

  createMiniPopupContent(city, gigs) {
    return `
      <div class="p-2 min-w-32">
        <h4 class="font-semibold text-sm text-gray-900 mb-1">${city}</h4>
        <p class="text-xs text-gray-600">
          ${gigs.length} concert${gigs.length > 1 ? 's' : ''}
        </p>
      </div>
    `
  }

  scrollToMainMap() {
    if (this.hasMainMapTarget) {
      this.mainMapTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }

  async drawDrivingRoute(coordinates) {
    if (coordinates.length < 2 || !this.miniMap) return

    try {
      // Mapbox Directions API accepts up to 25 waypoints
      // Format: lng,lat;lng,lat;...
      const waypointsString = coordinates
        .map(coord => `${coord[0]},${coord[1]}`)
        .join(';')

      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${waypointsString}?geometries=geojson&overview=full&access_token=${this.authToken}`
      )

      const data = await response.json()

      if (data.routes && data.routes.length > 0) {
        const route = data.routes[0].geometry

        // Add the route as a layer on the mini map
        if (this.miniMap.getSource('route')) {
          this.miniMap.getSource('route').setData(route)
        } else {
          this.miniMap.addSource('route', {
            type: 'geojson',
            data: route
          })

          this.miniMap.addLayer({
            id: 'route',
            type: 'line',
            source: 'route',
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': '#10b981',
              'line-width': 3,
              'line-opacity': 0.7
            }
          })
        }
      }
    } catch (error) {
      console.error('Erreur lors du tracé de l\'itinéraire:', error)
    }
  }

  get authToken() {
    const meta = document.head.querySelector('meta[name="mapbox_token"]')
    if (!meta) {
      console.error("Token Mapbox non trouvé dans les meta tags")
      return null
    }
    return meta.content
  }
}